/**
 * CASTLE ATLAS — AI Prompt Engineering Landing Page (NODE MAP REDESIGN, v6)
 * ═════════════════════════════════════════════════════════════
 * v6 — edge alignment + radar placement.
 *
 * NOTE: the reference diagrams (`page_section_layout_v4`,
 * `node_map_zigzag_routing_v2`) named in the v6 brief were NOT attached to
 * the message that produced this file. Built from the written spec only.
 *
 * v6 changes:
 *   1. Node map now shares EXACT left/right edges with the skill profile
 *      belt — not just "close." Root cause of the old mismatch: the map's
 *      lesson nodes were centered within their own column band (leaving an
 *      empty margin before the first/after the last node), the node's
 *      fixed-pixel width didn't scale with the container the way its
 *      percentage-based position did, and the canvas wrapper had its own
 *      `min-width: 1100px` + `overflow-x: auto` which could make it wider
 *      than — and independently scrollable from — its padded parent.
 *      Fixed by: (a) dropping the forced min-width/scroll, the canvas is
 *      now `width: 100%` of the same padded wrapper the belt uses; (b)
 *      making node width a percentage of the canvas (not fixed px), so it
 *      scales in lockstep with position; (c) redefining node x-positions so
 *      column 0 (phase 1) sits with its LEFT edge at exactly 0% and the
 *      last column (phase 6 / Infinite Reps) sits with its RIGHT edge at
 *      exactly 100%, with the remaining columns evenly distributed between
 *      those two anchors. Since the belt, the "Learning Path" header, and
 *      the node map all live inside identically-padded wrapper divs, this
 *      makes phase 1's left edge and phase 6/Infinite Reps's right edge
 *      land at the same real pixel X as the belt's own border — exactly,
 *      at any viewport width, with no runtime measurement needed.
 *   2. Radar moved into the manifest panel's own leftover space, replacing
 *      v5's centered-overlay approach entirely. `RadarOverlay` (the fixed/
 *      backdrop/centered-card component) is gone — no fixed positioning, no
 *      backdrop, no origin-tracking. The belt's radar tab still just flips
 *      `radarOpen`; when true, `ManifestRadar` renders inline as the second
 *      child of the manifest column (below `ManifestPanel`), filling
 *      whatever vertical space is left within the manifest's full-height
 *      column via `flex: 1`. When closed, that space is just an empty
 *      flex-1 spacer.
 *
 * Unchanged from v5 (still current):
 *   - No outer box, no box around top strip / manifest panel / node map /
 *     legend — only the skill profile belt is boxed.
 *   - Full-height background fix (dark bg + min-height:100vh on the actual
 *     page root, not a content wrapper).
 *   - Alternating phase-label placement per the tier table, each label
 *     flush with its own column's lesson nodes.
 *   - Full lesson names, no ellipsis truncation.
 *   - Infinite Reps disconnected from the path (no connector to lesson 13).
 *   - Node tier table, real `lessons` table having only 3 rows (still OK
 *     decorative — see caveat B), no changes to other landing pages, no
 *     per-node inspector rail.
 *
 * Design tokens and curriculum data stay local to this file — Supabase's
 * `skills` table only stores flat counts, not phase/lesson structure.
 *
 * CAVEATS (read before tuning numbers):
 *
 * A. No visual reference was available for this file's build history —
 *    every layout/visual decision is a best-effort interpretation of the
 *    WRITTEN spec. This pass's geometry (tier table, alternating label
 *    rule) was precise enough to build with real confidence; exact
 *    spacing/proportions are still a guess pending the actual diagrams.
 *
 * B. Curriculum/data mismatch — STILL PARTIAL, IMPROVED THIS PASS. Only
 *    lesson IDs 1–3, plus two more created to host the first gold-standard
 *    reps (negative-constraints, reusable-prompts — see the v2 engine-fixes
 *    round), exist as real rows in the `lessons` table that
 *    `user_progress.lesson_id` FKs against. The rest of the 13-lesson map
 *    (and the Infinite Reps node) is still decorative — it renders but
 *    cannot record real progress until those lesson rows exist too. Not
 *    scoped down here; a separate curriculum-seeding pass closes the rest.
 *
 * C. A3/A4 fix (engine-fixes round): per-lesson rep counts are no longer a
 *    single hardcoded EXERCISES_PER_LESSON=5 — that was the actual bug
 *    behind "pass a rep, map doesn't update": a lesson seeded with its
 *    real (smaller) rep count could never reach getLessonDone(lid) >= 5,
 *    so it never showed complete no matter what a learner did. Every
 *    completion check below now reads `lessons.rep_count` per lesson (see
 *    hooks/useLessons.js's repCountFor()), falling back to 5 only for
 *    lessons that don't have a real row yet (caveat B). `EXERCISES_PER_LESSON`
 *    itself is dead below this point — kept as repCountFor's own default,
 *    not referenced directly in this file anymore.
 *
 * D. "Sealed" nodes and the Infinite Reps lock are soft, decorative locks
 *    only — no server-side gate exists (PromptSandbox's lessonGate already
 *    returns `true` unconditionally).
 *
 * E. XP badge: no real XP field exists anywhere — locked/coming-soon pill,
 *    no fabricated number (unchanged since round 1).
 *
 * Real path: frontend/src/features/ai-prompting/pages/PromptLanding.jsx
 */

import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle, ArrowRight, Flame, Lock, Play,
  Radar as RadarIcon, Map, BookOpen,
} from 'lucide-react';
import { useProgress } from '@hooks/useProgress';
import { useStreak } from '@hooks/useStreak';
import { usePageTitle } from '@hooks/usePageTitle';
import { useForceSurface } from '@hooks/useSurface';
import { useSkills } from '@hooks/useSkills';
import { useMediaQuery } from '@hooks/useMediaQuery';
import {
  RadarChart, PolarGrid, PolarAngleAxis, Radar,
  ResponsiveContainer, Tooltip,
} from 'recharts';
import { TT, PHASES, PHASE_ICONS, UNLOCK_ALL_DEV } from '@features/ai-prompting/terminalTokens';
import { TermWindow, TermLine, TermComment, TermKeyVal, ProgressRing, SkeletonLoader } from '@features/ai-prompting/components/Term';
import { useLessons, lessonName, repCountFor, lessonNumberFor } from '@hooks/useLessons';
import { useDueReviews } from '@hooks/useDueReviews';
import { useTechniques } from '@hooks/useTechniques';
import { useTechniqueMastery } from '@hooks/useTechniqueMastery';

// v10 — mobile & tablet. ONE breakpoint drives every "isWide" layout choice
// on this page (node map: zigzag vs. vertical tree; belt: row vs. stacked;
// legend: row vs. 2x2 grid) — deliberately not three independently-tuned
// thresholds, so there's no untested combination of "wide map + narrow
// belt" in between. Measured live at 1280px (a common laptop width): with
// the app's persistent 240px sidebar (`<aside class="hidden md:flex">`,
// shown at Tailwind's md/768px+) plus the 380px manifest column, the zigzag
// map only had 568px to work with — nodes render at 81px wide with visibly
// colliding two-line names. 1400px was chosen (over keeping 1100px and
// narrowing the manifest column) as the fix.
const WIDE_QUERY = '(min-width: 1400px)';

// v11 — a second, independent breakpoint: whether the manifest column runs
// side-by-side with the right column or stacks above it (unrelated to
// WIDE_QUERY, which governs the map/belt/legend). This is also where
// ManifestRadar mounts: side-by-side, it lives in the manifest column as
// before; stacked, mounting it there would render the chart off-screen
// above the belt's radar button that opens it (the v10-flagged bug), so it
// mounts in the right column instead, directly under SkillBelt. The CSS
// breakpoint below is the exact one-pixel-lower complement (879px) so the
// two queries never disagree at the boundary pixel itself.
const MANIFEST_SIDE_BY_SIDE_QUERY = '(min-width: 880px)';

// Flattened, ordered lesson sequence — module-level since PHASES is static.
// Drives node-map ordinal logic (current / available / sealed).
const FLATTENED_LESSONS = PHASES.flatMap((phase, phaseIdx) =>
  phase.lessons.map(lessonId => ({ lessonId, phaseIdx, phaseNum: phase.num, phaseColor: phase.color }))
);

// Prompt 5, item 5: the radar used to run on 6 hardcoded, invented
// categories (Clarity/Context/Role Design/Output Ctrl/Iteration/Systems)
// mapped from a per-lesson-id table that mapped to nothing real. We have
// 13 real techniques with a live mastery score each (technique_mastery) —
// driving 13 axes directly would be unreadable on a radar, so this
// groups them by PHASE into 5 axes instead (chosen over, say, grouping
// by difficulty, because phase is the grouping the rest of this page
// already organizes around — the learning path, the manifest counts, the
// phase labels on the map — so the radar reads as "your standing per
// phase of the path you can see," not a second, unrelated taxonomy).
// The grouping itself comes from techniques.phase (real data), not a
// hardcoded list — see buildPhaseRadarData below.
//
// Prompt 6, item 5: recharts clips PolarAngleAxis labels at a 5-axis
// pentagon's left/right vertices once a label runs much past ~16
// characters, regardless of chart margin — "Professional application"
// (25 chars) and "Systems and voice" (18 chars) were both getting cut.
// These are display-only shortenings for the radar specifically — they
// do NOT rename the phases themselves (phase.label stays the full,
// descriptive version everywhere else: map headers, breadcrumbs, the
// manifest panel), kept recognizable per instruction.
const RADAR_AXIS_LABEL = {
  1: 'Foundations',
  2: 'Control patterns',
  3: 'Applications',
  4: 'Reliability',
  5: 'Systems & voice',
};
const radarLabelFor = (phase) => RADAR_AXIS_LABEL[phase.num] ?? phase.label;

const buildPhaseRadarData = (techniques, mastery) => {
  const masteryByTechniqueId = Object.fromEntries(mastery.map(m => [m.technique_id, m.mastery_score]));
  return PHASES.map(phase => {
    const phaseTechniques = techniques.filter(t => t.phase === phase.num);
    const avg = phaseTechniques.length
      ? Math.round(phaseTechniques.reduce((sum, t) => sum + (masteryByTechniqueId[t.id] ?? 0), 0) / phaseTechniques.length)
      : 0;
    return { skill: radarLabelFor(phase), value: avg, full: 100 };
  });
};

/* ═══ NODE MAP — a true snake, fully data-driven (prompt 6, item 3,
   replacing prompt 5's per-phase-column zigzag entirely). The old design
   gave every PHASE its own column and zigzagged vertically within it —
   which meant crossing from one phase's column to the next was a long
   diagonal jump whenever the two phases' lessons landed on different
   tiers, and the visual order didn't track the real 1-13 sequence at all.
   This lays out ALL 14 nodes (13 lessons + Infinite Reps) as one
   continuous snake over a fixed-width grid: fill a row left to right,
   drop down one row, fill the next row right to left, repeat. Every
   consecutive pair is therefore either same-row-adjacent-column
   (horizontal) or same-column-adjacent-row (vertical) — never a
   diagonal, and always physically adjacent, by construction, regardless
   of how many lessons any phase has or how many phases exist. ═══ */
const SNAKE_COLS = 5;
const SEQUENCE_LENGTH = FLATTENED_LESSONS.length + 1; // 13 lessons + Infinite Reps
const SNAKE_ROWS = Math.ceil(SEQUENCE_LENGTH / SNAKE_COLS);

// Row-major index -> {row, col}, snaking direction alternating per row.
const gridPosForIndex = (i) => {
  const row = Math.floor(i / SNAKE_COLS);
  const colInRow = i % SNAKE_COLS;
  const col = row % 2 === 0 ? colInRow : (SNAKE_COLS - 1 - colInRow);
  return { row, col };
};

const MAP_VIEWBOX_W = 1400;
const NODE_W_VB = 200; // viewbox units
const nodeWidthPct = (NODE_W_VB / MAP_VIEWBOX_W) * 100;

// Column 0's node center sits at exactly NODE_W_VB/2 (left edge = 0%); the
// last column's node center sits at exactly MAP_VIEWBOX_W - NODE_W_VB/2
// (right edge = 100%) — same edge-alignment rule as before, now keyed by
// grid column instead of phase index.
const colXForCol = (col) =>
  NODE_W_VB / 2 + col * (MAP_VIEWBOX_W - NODE_W_VB) / (SNAKE_COLS - 1);

// Vertical rhythm: TOP_PAD_VB leaves room for a phase label sitting above
// row 0's nodes; ROW_PITCH_VB is the distance between consecutive row
// centers, generous enough that a phase-label pill above a row-starting
// node never touches the row above it or the connector feeding into it
// (see the label's own left-anchored, off-centerline positioning in
// LessonNode, which is what actually keeps it clear of vertical
// connectors — the pitch just keeps it clear of the PREVIOUS row).
const TOP_PAD_VB = 60;
const ROW_PITCH_VB = 170;
const BOTTOM_PAD_VB = 70;
const rowCenterY = (row) => TOP_PAD_VB + row * ROW_PITCH_VB;
const MAP_VIEWBOX_H = TOP_PAD_VB + (SNAKE_ROWS - 1) * ROW_PITCH_VB + BOTTOM_PAD_VB;

const NODE_LAYOUT = FLATTENED_LESSONS.map((l, i) => {
  const { row, col } = gridPosForIndex(i);
  const isPhaseStart = FLATTENED_LESSONS.findIndex(x => x.phaseNum === l.phaseNum) === i;
  return { ...l, row, col, x: colXForCol(col), y: rowCenterY(row), isPhaseStart };
});

// Infinite Reps is the very next slot in the SAME snake sequence, right
// after lesson 13 — not a separate decorative tier assignment. That
// alone guarantees it renders strictly last with nothing after it,
// regardless of how many lessons/phases exist (item 3's explicit
// requirement). It still carries no connector to/from it (unchanged from
// prior rounds — it isn't part of the real lesson progression).
const INFINITE_REPS_INDEX = FLATTENED_LESSONS.length;
const infiniteRepsGridPos = gridPosForIndex(INFINITE_REPS_INDEX);
const INFINITE_REPS_NODE = {
  id: 'infinite-reps',
  x: colXForCol(infiniteRepsGridPos.col),
  y: rowCenterY(infiniteRepsGridPos.row),
};

// Connectors cover ONLY the 13 real lessons' internal transitions
// (Infinite Reps deliberately excluded — see above). `vertical` = same
// column (a row-transition); otherwise horizontal (adjacent column, same
// row) — true by construction for any two consecutive snake indices.
const CONNECTORS = NODE_LAYOUT.slice(1).map((to, i) => {
  const from = NODE_LAYOUT[i];
  return { from, to, vertical: from.col === to.col };
});

// Trim amount for each connector's endpoints, so the line stops at the
// node's edge instead of its exact center. NODE_W_VB_HALF is exact (node
// width is already a viewbox-unit constant); NODE_H_VB_HALF is a
// deliberate approximation since node height is CSS-content-driven, not
// viewbox-based.
const NODE_W_VB_HALF = NODE_W_VB / 2;
const NODE_H_VB_HALF = 32;

/* ══════════════════════════════════════════
   TOP STRIP — no box, just a border-bottom divider (correction 1)
══════════════════════════════════════════ */
// v11: dropped the "skills" segment — "atlas" going to the dashboard while
// "skills" went to the skills index was two different mental models in one
// trail. Now just dashboard / ai-prompting.
const Breadcrumb = ({ navigate }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: TT.fontMono, fontSize: 12 }}>
    <span onClick={() => navigate('/dashboard')} style={{ color: TT.textDim, cursor: 'pointer' }}>dashboard</span>
    <span style={{ color: TT.textDim }}>/</span>
    <span style={{ color: TT.orange, fontWeight: 700 }}>ai-prompting</span>
  </div>
);

/* ══════════════════════════════════════════
   REVIEWS BADGE — prompt 4 Part B4. A count of what's due for review,
   visible on the map, never gating progression on the main path. Clicking
   it opens a small panel listing due items, each linking straight to the
   rep (a rep the learner already passed — see reviewQueueService.js).
══════════════════════════════════════════ */
const ReviewsBadge = ({ count, reviews, loading, open, onToggle, navigate }) => {
  if (loading || count === 0) return null;
  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={onToggle}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 99,
          background: 'rgba(56,189,248,0.1)', border: `1px solid ${TT.cyan}`, cursor: 'pointer',
          fontFamily: TT.fontMono, fontSize: 11, fontWeight: 800, color: TT.cyan,
        }}
      >
        {count} due for review
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 20, width: 320,
          background: TT.bgSurface, border: `1px solid ${TT.border}`, borderRadius: TT.radius.sm,
          padding: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}>
          <div style={{ fontFamily: TT.fontMono, fontSize: 10, color: TT.textDim, textTransform: 'uppercase', marginBottom: 8 }}>
            review queue
          </div>
          {reviews.map(r => (
            <div
              key={r.techniqueId}
              onClick={() => navigate(`/skills/ai-prompting/lessons/${r.lessonId}/reps/${r.position}`)}
              style={{
                padding: '8px 6px', borderRadius: TT.radius.sm, cursor: 'pointer',
                borderBottom: `1px solid ${TT.border}`,
              }}
            >
              <div style={{ fontSize: 12, color: TT.text, fontWeight: 700 }}>{r.techniqueName ?? r.techniqueKey}</div>
              <div style={{ fontSize: 11, color: TT.textMuted, fontFamily: TT.fontMono }}>{r.repTitle}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const StreakBadge = ({ streak }) => (
  <div style={{
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 99,
    background: 'rgba(234,88,12,0.1)', border: `1px solid ${TT.orangeGlow}`,
  }}>
    <Flame size={12} color={TT.orange} />
    <span style={{ fontSize: 11, fontWeight: 800, color: TT.orange, fontFamily: TT.fontMono }}>
      {streak > 0 ? `${streak}d streak` : 'no streak yet'}
    </span>
  </div>
);

/* ══════════════════════════════════════════
   MANIFEST PANEL — no box; a single border-right divider (applied by its
   parent) is the only separation from the right column.
══════════════════════════════════════════ */
const ManifestPanel = ({ skill, resumeTarget, lessonsPct, lessonsComplete, lessonsTotal, onResume, lessons }) => {
  return (
  <div style={{ padding: '22px 20px', flexShrink: 0, boxSizing: 'border-box' }}>
    <TermComment>Castle Atlas v2.1.0 - AI Prompt Engineering Skill</TermComment>
    <TermLine prompt="$">
      <span style={{ color: TT.orange }}>cat</span>
      <span style={{ color: TT.textMuted }}> /etc/skill-manifest.json</span>
    </TermLine>
    <div style={{ marginLeft: 24, marginTop: 6, marginBottom: 16 }}>
      <TermKeyVal label="name" value='"AI Prompt Engineering"' valueColor={TT.green} />
      <TermKeyVal label="status" value='"in_progress"' valueColor={TT.amber} />
      <TermKeyVal label="phases" value={skill.phases.length} valueColor={TT.purple} />
      <TermKeyVal label="modules" value={`${lessonsComplete}/${lessonsTotal}`} valueColor={TT.cyan} />
      <TermKeyVal label="overall" value={`${lessonsPct}%`} valueColor={TT.orange} />
    </div>

    {/* Resume CTA — terminal-command styling: $ ./resume · {Lesson} → */}
    {resumeTarget ? (
      <div style={{ marginBottom: 16 }}>
        <TermLine prompt="$" style={{ marginBottom: 8 }}>
          <span style={{ color: TT.orange }}>./resume</span>
          <span style={{ color: TT.textMuted }}> --module {lessonNumberFor(lessons, resumeTarget.lessonId)} --rep {resumeTarget.repNum}</span>
        </TermLine>
        <button
          onClick={onResume}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', background: 'transparent', color: TT.orange,
            border: `1px solid ${TT.orange}`, borderRadius: TT.radius.sm,
            fontSize: 13, fontWeight: 700, cursor: 'pointer',
            fontFamily: TT.fontMono, transition: 'all 0.2s ease',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = TT.orange;
            e.currentTarget.style.color = '#000';
            e.currentTarget.style.boxShadow = `0 0 16px ${TT.orangeGlow}`;
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = TT.orange;
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <span style={{ color: TT.textDim }}>$</span> ./resume ·{' '}
          <span style={{ color: TT.amber }}>{lessonName(lessons, resumeTarget.lessonId)}</span>
          <ArrowRight size={13} />
        </button>
        <p style={{ fontSize: 11, color: TT.textDim, marginTop: 8}}>
          Rep {resumeTarget.repNum} of {repCountFor(lessons, resumeTarget.lessonId)} · Phase {resumeTarget.phaseNum}
        </p>
      </div>
    ) : (
      <div style={{ marginBottom: 16 }}>
        <TermLine prompt="$" style={{ marginBottom: 8 }}>
          <span style={{ color: TT.green }}>./review</span>
          <span style={{ color: TT.textMuted }}> --all-complete</span>
        </TermLine>
        <button
          onClick={onResume}
          style={{
            marginLeft: 24, display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', background: TT.green, color: '#000',
            border: 'none', borderRadius: TT.radius.sm,
            fontSize: 13, fontWeight: 700, cursor: 'pointer',
            fontFamily: TT.fontMono, transition: 'all 0.2s ease',
          }}
        >
          <CheckCircle size={13} /> Review All Material <ArrowRight size={13} />
        </button>
      </div>
    )}
  </div>
  );
};

/* ══════════════════════════════════════════
   SKILL PROFILE BELT — the ONLY boxed region (bordered + dot-chrome).
   radarBtnRef forwarded so the overlay's open animation can travel from
   near this tab (correction 6).
══════════════════════════════════════════ */
const BeltStat = ({ label, pct }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <span style={{ fontSize: 11, color: TT.textMuted, fontFamily: TT.fontMono }}>{label}</span>
      <span style={{ fontSize: 11, fontWeight: 800, color: TT.text }}>{pct}%</span>
    </div>
    <div style={{ height: 4, background: TT.bgSurface, borderRadius: 2, overflow: 'hidden' }}>
      <div style={{
        height: '100%', width: `${pct}%`,
        background: `linear-gradient(90deg,${TT.orange},${TT.amber})`, borderRadius: 2,
        transition: 'width 0.8s cubic-bezier(0.22,1,0.36,1)',
      }} />
    </div>
  </div>
);

// Hexagon shape shared by the belt's radar toggle below — echoes the
// actual recharts RadarChart it opens (a 6-axis hexagonal grid) rather
// than an arbitrary icon.
const HEX_POINTS = '12,3 19.79,7.5 19.79,16.5 12,21 4.21,16.5 4.21,7.5';

// Idle: reads as a small solid hexagon. Toggling radarOpen fades it to a
// stroke-only outline of the same shape (fill -> outline, echoing the
// chart it opens), reversing on close. prefers-reduced-motion gets an
// instant swap instead of the animated morph.
const RadarHexIcon = ({ open, size = 16 }) => {
  const reduceMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <polygon
        points={HEX_POINTS}
        fill="currentColor"
        stroke="currentColor"
        style={{
          fillOpacity: open ? 0 : 1,
          strokeWidth: open ? 2 : 0,
          transition: reduceMotion ? 'none' : 'fill-opacity 0.32s ease, stroke-width 0.32s ease',
        }}
      />
    </svg>
  );
};

// Below isWide (see the module-level WIDE_QUERY threshold), the belt
// restructures to ring+radar-button on one row (space-between, no dead
// space to the right of either) with the stats grid dropping to a full-
// width row below — instead of the old stack that left both the ring and
// the button with wasted space beside them. At/above the threshold this
// renders byte-for-byte the same markup as before.
const SkillBelt = ({ pct, radarData, radarOpen, onToggleRadar, isWide }) => {
  // Maps straight over radarData (one entry per phase — see
  // buildPhaseRadarData) instead of two hardcoded 3-item column lists, so
  // the grid always matches however many phases actually exist.
  const statsGrid = (
    <div style={{ flex: 1, minWidth: isWide ? 240 : '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 28px' }}>
      {radarData.map(d => <BeltStat key={d.skill} label={d.skill} pct={d.value} />)}
    </div>
  );

  // Hexagon (not lucide's Radar, which reads as a fingerprint at 16px —
  // concentric arcs with no vertex structure) previews the actual chart
  // shape it opens. Swapped in ManifestRadar's header too for consistency.
  const radarBtn = (
    <button
      onClick={onToggleRadar}
      title="View full skill radar"
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
        background: radarOpen ? `${TT.orange}18` : TT.bgSurface,
        border: `1px solid ${radarOpen ? TT.orange : TT.border}`,
        borderRadius: TT.radius.md, padding: '12px 12px', cursor: 'pointer',
        color: radarOpen ? TT.orange : TT.textMuted, transition: TT.trans, flexShrink: 0,
        minWidth: 44, minHeight: 44,
      }}
    >
      <RadarHexIcon open={radarOpen} size={16} />
      <span style={{ fontSize: 8, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Radar</span>
    </button>
  );

  if (!isWide) {
    return (
      <TermWindow title="castle-atlas --skill-profile">
        <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <ProgressRing pct={pct} size={72} stroke={6} />
            {radarBtn}
          </div>
          {statsGrid}
        </div>
      </TermWindow>
    );
  }

  return (
    <TermWindow title="castle-atlas --skill-profile">
      <div style={{
        position: 'relative', padding: '20px 22px', display: 'flex', alignItems: 'center',
        gap: 24, flexWrap: 'wrap',
      }}>
        <ProgressRing pct={pct} size={72} stroke={6} />
        {statsGrid}
        {radarBtn}
      </div>
    </TermWindow>
  );
};

/* ══════════════════════════════════════════
   MANIFEST RADAR — lives in the manifest panel's own leftover vertical
   space, toggled by the belt's radar tab (correction 2, v6). No fixed
   positioning, no backdrop, no centering-as-a-modal — replaces v5's
   overlay entirely. When closed, renders as an empty flex-1 spacer so the
   manifest column still fills its full height.
   v7 correction 3: chart is a fixed (not flex-grow) size, and the whole
   header+chart group is vertically centered (justifyContent:'center' on
   the flex:1 wrapper) within the leftover space, instead of sitting flush
   at its top.
   v8 correction 6: chart height/margins revised up to ~30% bigger than the
   original v6 baseline (140/10/28 → 182/13/36), not the 20% v7 applied.
   v9 correction 2: tick fontSize reverted to the original 9 — the 30%
   chart-size bump had scaled the axis label text right along with it,
   making the labels visibly bigger than the "SKILL RADAR" header above
   them. Text size and chart/shape size scale independently: only the
   height/margin trio above should track the 30% figure.
   v11 correction: justifyContent back to 'flex-start' — reverses v7
   correction 3 deliberately. Centering in the leftover space meant that on
   a tall or half-width window, the chart could park hundreds of pixels
   below the manifest text with a huge void above it. It now opens directly
   beneath the manifest text at every width; leftover space falls below it
   instead. See MANIFEST_SIDE_BY_SIDE_QUERY above for where this component
   mounts when the manifest column is stacked instead of side-by-side.
══════════════════════════════════════════ */
const ManifestRadar = ({ open, hasStarted, radarData }) => {
  const ghostData = PHASES.map(phase => ({ skill: radarLabelFor(phase), value: 75, full: 100 }));

  if (!open) return <div style={{ flex: 1 }} />;

  return (
    <div style={{
      flex: 1, minHeight: 0, borderTop: `1px solid ${TT.border}`,
      padding: '16px 20px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <RadarIcon size={13} color={TT.orange} />
        <span style={{ fontSize: 10, fontWeight: 800, color: TT.textDim, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Skill Radar
        </span>
      </div>
      <div style={{ height: 267, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={hasStarted ? radarData : ghostData} margin={{ top: 17, right: 62, bottom: 17, left: 62 }}>
            <PolarGrid stroke={TT.border} />
            <PolarAngleAxis dataKey="skill" tick={{ fill: TT.textMuted, fontSize: 9, fontFamily: TT.fontMono, dy: 3 }} />
            {hasStarted ? (
              <>
                <Radar name="Full" dataKey="full" stroke="transparent" fill="rgba(234,88,12,0.06)" fillOpacity={1} />
                <Radar name="You" dataKey="value" stroke={TT.orange} fill={TT.orange}
                  fillOpacity={0.22} strokeWidth={2}
                  isAnimationActive animationBegin={80} animationDuration={700} animationEasing="ease-out" />
                <Tooltip formatter={v => [`${v}%`, 'Your level']}
                  contentStyle={{ background: TT.bgSurface, border: `1px solid ${TT.border}`, borderRadius: 8, fontSize: 11, fontFamily: TT.fontMono, color: TT.text }} />
              </>
            ) : (
              <Radar name="Mastery" dataKey="value" stroke={TT.orange} fill={TT.orange}
                fillOpacity={0.08} strokeWidth={1.5} strokeDasharray="4 4" isAnimationActive={false} />
            )}
          </RadarChart>
        </ResponsiveContainer>
      </div>
      {!hasStarted && (
        <p style={{ textAlign: 'center', fontSize: 10, color: TT.textDim, marginTop: 6 }}>
          Preview — complete reps to build your real profile.
        </p>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════
   LESSON NODE — full name always visible now (correction 4): no nowrap/
   ellipsis, wraps onto 2 lines, box widened to fit. Width is a percentage
   of the canvas (nodeWidthPct, defined above) so it scales in lockstep
   with position — needed for the exact edge alignment in v6 correction 1.
══════════════════════════════════════════ */
const LessonNode = ({ lessonId, phaseColor, state, onClick, lessons, isPhaseStart, phaseNum }) => {
  const [hov, setHov] = useState(false);
  const name = lessonName(lessons, lessonId);
  const number = lessonNumberFor(lessons, lessonId);
  const clickable = state !== 'sealed';

  const style = {
    lit:       { bg: phaseColor, border: phaseColor, text: '#fff', eyebrow: 'rgba(255,255,255,0.72)' },
    current:   { bg: TT.bgSurface, border: TT.orange, text: TT.text, eyebrow: TT.orange },
    available: { bg: TT.bg, border: `${phaseColor}80`, text: TT.text, eyebrow: TT.textDim },
    sealed:    { bg: TT.bg, border: TT.border, text: TT.textDim, eyebrow: TT.textDim },
  }[state];

  // Prompt 6, item 3: a phase label attached directly to its first
  // lesson's own node — not a separately-positioned element elsewhere on
  // the canvas that has to be kept in sync with wherever that lesson
  // happens to land. Anchored to the node's left edge with a capped width
  // (well under half the node's width) so it never crosses the column's
  // centerline, which is exactly where a vertical connector coming into
  // this node from the row above would run — that's what keeps it clear
  // of connectors without needing to reason about every possible snake
  // position case by case.
  const PhaseIcon = PHASE_ICONS[(phaseNum ?? 1) - 1] ?? BookOpen;
  const phase = PHASES[(phaseNum ?? 1) - 1];

  return (
    <div
      onClick={clickable ? onClick : undefined}
      onMouseEnter={() => clickable && setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: '10px 12px', borderRadius: TT.radius.md, width: '100%', boxSizing: 'border-box',
        background: style.bg,
        border: state === 'sealed' ? `1.5px dashed ${style.border}` : `1.5px solid ${style.border}`,
        cursor: clickable ? 'pointer' : 'not-allowed',
        boxShadow: state === 'current'
          ? `0 0 0 3px ${TT.orangeGlow}, 0 0 24px ${TT.orangeGlow}`
          : hov ? '0 4px 16px rgba(0,0,0,0.4)' : 'none',
        transform: hov && clickable ? 'translateY(-2px)' : 'none',
        transition: TT.trans, opacity: state === 'sealed' ? 0.55 : 1,
        // Prompt 6, item 4: every state needs a genuinely opaque node
        // above the connector layer, not just a border. z-index alone
        // does nothing if the SVG connector layer sits at a higher
        // stacking order than expected in some browser/layout
        // combination — set explicitly here rather than relying on DOM
        // order alone.
        zIndex: 2,
      }}
    >
      {isPhaseStart && phase && (
        <div style={{
          position: 'absolute', top: -24, left: 0, zIndex: 3,
          // 95%, not a tighter cap: checked every phase-start node against
          // CONNECTORS for the current 13-lesson/5-column snake and none
          // of them are a row-transition target (the only case where a
          // vertical connector would run through the space directly above
          // a node) — they all sit mid-row, fed by a horizontal connector
          // that runs through the node's own vertical center, well below
          // this label. Still capped under 100%, not removed, in case a
          // future lesson-count change ever lands a phase start exactly
          // on a row-transition node.
          display: 'flex', alignItems: 'center', gap: 5, maxWidth: '95%',
        }}>
          <div style={{ width: 16, height: 16, borderRadius: 5, background: `${phaseColor}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <PhaseIcon size={9} color={phaseColor} />
          </div>
          <span style={{ fontSize: 9, fontWeight: 800, color: TT.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {phase.label}
          </span>
        </div>
      )}
      <div style={{
        width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        // item 4: 'available'/'sealed' used to leave this circle fully
        // transparent — a small window straight through to whatever sits
        // behind the node (the SVG connector layer). Opaque now, matching
        // the node's own background so the seam is invisible when nothing
        // is behind it, and nothing shows through when something is.
        background: state === 'lit' ? 'rgba(255,255,255,0.22)' : state === 'current' ? TT.orange : style.bg,
        border: state === 'available' ? `1.5px solid ${phaseColor}80` : state === 'sealed' ? `1.5px dashed ${TT.border}` : 'none',
      }}>
        {state === 'lit' && <CheckCircle size={13} color="#fff" />}
        {state === 'current' && <Play size={10} color="#000" fill="#000" />}
        {state === 'sealed' && <Lock size={10} color={TT.textDim} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 8.5, fontWeight: 700, color: style.eyebrow, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
          {`Module ${number}`}
        </div>
        {/* No nowrap/ellipsis — full name, wraps onto 2 lines if needed. */}
        <div style={{ fontSize: 12, fontWeight: 700, color: style.text, lineHeight: 1.3 }}>
          {name}
        </div>
      </div>
    </div>
  );
};

// Infinite Reps — the terminal node. Neutral gray, dashed border, lock
// glyph, always non-clickable (caveat D). No connecting line at all
// (correction 5) — sits standalone near the end of the map.
const InfiniteRepsNode = ({ unlocked }) => (
  <div
    title={unlocked ? 'Unlocked — not wired yet' : 'Unlocks once every module is complete'}
    style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 12px', borderRadius: TT.radius.md,
      background: TT.bg,
      border: `1.5px dashed ${unlocked ? TT.green : TT.gray}`,
      opacity: unlocked ? 0.85 : 0.55, cursor: 'not-allowed',
    }}
  >
    <div style={{
      width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: `1.5px dashed ${unlocked ? TT.green : TT.gray}`,
    }}>
      <Lock size={10} color={unlocked ? TT.green : TT.gray} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 8.5, fontWeight: 700, color: TT.textDim, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {unlocked ? 'Unlocked' : 'Locked'}
      </div>
      <div style={{ fontSize: 12, fontWeight: 700, color: unlocked ? TT.green : TT.gray, lineHeight: 1.3 }}>
        Infinite Reps
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════
   NODE MAP CANVAS — no box, floats directly on the page background. Width
   is 100% of the same padded wrapper the belt uses, so its edges are the
   belt's edges. Prompt 6, item 3: this is now a true snake over
   NODE_LAYOUT's grid positions (see the constants above) instead of a
   per-phase-column zigzag — phase labels are attached to their first
   lesson's own node (see LessonNode) rather than positioned here
   separately. The connector layer (SVG, z-index 1) sits explicitly below
   the node layer (z-index 2, set on LessonNode/InfiniteRepsNode's own
   wrapper) — item 4's opaque-background-plus-z-index fix.
══════════════════════════════════════════ */
const NodeMapCanvas = ({ getLessonDone, getNodeState, onNodeClick, courseComplete, lessons }) => {
  const isTraveled = (fromNode) => getLessonDone(fromNode.lessonId) >= repCountFor(lessons, fromNode.lessonId);

  return (
    <div style={{ position: 'relative', width: '100%', aspectRatio: `${MAP_VIEWBOX_W} / ${MAP_VIEWBOX_H}` }}>
      <svg viewBox={`0 0 ${MAP_VIEWBOX_W} ${MAP_VIEWBOX_H}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 1 }}>
        {CONNECTORS.map(({ from, to, vertical }, i) => {
          const traveled = isTraveled(from);
          const color = traveled ? TT.orange : (to.phaseColor || TT.gray);
          // Trim both ends to the node's edge, not its center — see
          // NODE_W_VB_HALF/NODE_H_VB_HALF above.
          let x1 = from.x, y1 = from.y, x2 = to.x, y2 = to.y;
          if (vertical) {
            const dir = to.y > from.y ? 1 : -1;
            y1 = from.y + dir * NODE_H_VB_HALF;
            y2 = to.y - dir * NODE_H_VB_HALF;
          } else {
            const dir = to.x > from.x ? 1 : -1;
            x1 = from.x + dir * NODE_W_VB_HALF;
            x2 = to.x - dir * NODE_W_VB_HALF;
          }
          return (
            <line
              key={i}
              x1={x1} y1={y1} x2={x2} y2={y2}
              stroke={color}
              strokeWidth={traveled ? 3 : 2.5}
              strokeDasharray={traveled ? 'none' : '7 6'}
              strokeLinecap="round"
              opacity={traveled ? 1 : 0.45}
              style={{ transition: 'stroke 0.4s ease, opacity 0.4s ease' }}
            />
          );
        })}
      </svg>

      {NODE_LAYOUT.map((n) => {
        const state = getNodeState(n.lessonId);
        return (
          <div
            key={n.lessonId}
            style={{
              position: 'absolute',
              left: `${(n.x / MAP_VIEWBOX_W) * 100}%`,
              top: `${(n.y / MAP_VIEWBOX_H) * 100}%`,
              transform: 'translate(-50%, -50%)', width: `${nodeWidthPct}%`, zIndex: 2,
            }}
          >
            <LessonNode
              lessonId={n.lessonId} phaseColor={n.phaseColor} state={state}
              onClick={() => onNodeClick(n.lessonId)} lessons={lessons}
              isPhaseStart={n.isPhaseStart} phaseNum={n.phaseNum}
            />
          </div>
        );
      })}

      {/* Terminal node — standalone, no connector, strictly the last slot
          in the same snake sequence (item 3). */}
      <div
        style={{
          position: 'absolute',
          left: `${(INFINITE_REPS_NODE.x / MAP_VIEWBOX_W) * 100}%`,
          top: `${(INFINITE_REPS_NODE.y / MAP_VIEWBOX_H) * 100}%`,
          transform: 'translate(-50%, -50%)', width: `${nodeWidthPct}%`, zIndex: 2,
        }}
      >
        <InfiniteRepsNode unlocked={courseComplete} />
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   VERTICAL TREE — the below-isWide replacement for NodeMapCanvas. The
   zigzag's 3-tier geometry is CSS-driven-height inside a fixed-aspect-ratio
   box; below ~1400px there isn't enough room for 6 columns of readable
   two-line node names (see the diagnosis above `WIDE_QUERY`), and it's not
   fixable with font/aspect-ratio tweaks — it needs a second, non-canvas
   layout. This is that layout: single column, natural block flow, full
   curriculum order top to bottom.

   `LessonNode`/`InfiniteRepsNode` are reused completely unchanged — only
   the positioning wrapper differs (a plain stacked div instead of absolute
   x/y placement on a viewBox). That wrapper is also where keyboard support
   lives: LessonNode itself has no tabIndex/onKeyDown (true in the existing
   zigzag too), so each node here gets wrapped in a focusable/keydown-
   activatable shell rather than editing LessonNode to add it.

   Ordering comes straight from FLATTENED_LESSONS/PHASES (the same single
   source of truth the zigzag uses) — no second hardcoded lesson list.
══════════════════════════════════════════ */
const TREE_CONNECTOR_H = 20;

// Traveled/untraveled follows the exact same rule as the zigzag's
// CONNECTORS: solid TT.orange once the FROM lesson is done, otherwise
// dashed in the TO lesson's phase color at 0.45 opacity. Reused for all
// three connector positions in the tree: lesson-to-lesson within a phase,
// and (v11) the two segments sandwiching a phase-header junction.
const TreeConnector = ({ traveled, color }) => (
  <div
    aria-hidden="true"
    style={{
      height: TREE_CONNECTOR_H,
      marginLeft: 24, // aligns under the 24px status circle
      borderLeft: traveled ? `2px solid ${TT.orange}` : `2px dashed ${color}`,
      opacity: traveled ? 1 : 0.45,
    }}
  />
);

// v11: measured live, not calculated — a <div> with only borderLeft:2px
// set (no explicit width) renders a 2px-wide box starting at marginLeft,
// so the stroke spans marginLeft to marginLeft+2 and its visual centre
// sits ONE PIXEL past marginLeft:24, at 25, not 24. The phase-header icon
// tile is indented to centre on that same measured line rather than the
// naive "24px tile centred on a line at marginLeft:24" arithmetic, which
// is off by a pixel from what's actually on screen.
const TREE_SPINE_CENTER_X = 25;
const TREE_PHASE_ICON_MARGIN = TREE_SPINE_CENTER_X - 12; // 13 — half the 24px tile

// Phase headers are now a JUNCTION ON THE SPINE, not a break in it (v11) —
// a previous pass connected lessons within a phase but let each phase
// float free, so the path visually restarted six times; the desktop
// zigzag doesn't have this problem because its horizontal jumps carry
// across phase boundaries. TreeConnector segments now run into and out of
// this header (rendered by the caller, not here — see VerticalTree) so the
// header reads as a stop on the line rather than a gap in it. The icon
// tile's left indent (TREE_PHASE_ICON_MARGIN) centres it on that line; the
// label and hairline rule shift right along with it since they're all
// children of the same indented row.
const TreePhaseHeader = ({ phase, idx }) => {
  const Icon = PHASE_ICONS[idx] ?? BookOpen;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: TREE_PHASE_ICON_MARGIN }}>
      <div style={{ width: 24, height: 24, borderRadius: 7, background: `${phase.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={12} color={phase.color} />
      </div>
      <span style={{ fontSize: 10, fontWeight: 700, color: `${phase.color}99`, textTransform: 'uppercase', letterSpacing: '0.07em', flexShrink: 0 }}>
        Phase {phase.num}
      </span>
      <span style={{ fontSize: 12, fontWeight: 800, color: TT.text, flexShrink: 0 }}>{phase.label}</span>
      <div style={{ flex: 1, height: 1, background: TT.border }} />
    </div>
  );
};

// Infinite Reps gets no connector above it (same rule as the desktop map)
// — a rule with no phase label instead, so the break is still visible.
// Deliberately detached, unlike the phase headers above.
const TreeSectionRule = () => (
  <div aria-hidden="true" style={{ display: 'flex', alignItems: 'center', margin: '28px 0 14px' }}>
    <div style={{ flex: 1, height: 1, background: TT.border }} />
  </div>
);

const VerticalTree = ({ getLessonDone, getNodeState, onNodeClick, courseComplete, lessons }) => {
  const isTraveled = (lessonId) => getLessonDone(lessonId) >= repCountFor(lessons, lessonId);

  return (
    <div>
      {FLATTENED_LESSONS.map((l, i) => {
        const prev = FLATTENED_LESSONS[i - 1];
        const newPhase = i === 0 || prev.phaseIdx !== l.phaseIdx;
        const state = getNodeState(l.lessonId);
        const clickable = state !== 'sealed';
        const name = lessonName(lessons, l.lessonId);
        // The junction's traveled state follows the lesson immediately
        // before it (same condition a same-phase TreeConnector would use)
        // — both the segment above and below the header share it, since
        // they sandwich the same point on the spine.
        const junctionTraveled = prev ? isTraveled(prev.lessonId) : false;

        return (
          <div key={l.lessonId}>
            {newPhase ? (
              <>
                {/* Phase 1 gets no segment above it — nothing precedes it. */}
                {i !== 0 && <TreeConnector traveled={junctionTraveled} color={l.phaseColor} />}
                <TreePhaseHeader phase={PHASES[l.phaseIdx]} idx={l.phaseIdx} />
                <TreeConnector traveled={junctionTraveled} color={l.phaseColor} />
              </>
            ) : (
              <TreeConnector traveled={isTraveled(prev.lessonId)} color={l.phaseColor} />
            )}
            <div
              tabIndex={clickable ? 0 : -1}
              role="button"
              aria-label={name}
              onKeyDown={(e) => {
                if (!clickable) return;
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNodeClick(l.lessonId); }
              }}
              className="tree-node-focus"
            >
              <LessonNode lessonId={l.lessonId} phaseColor={l.phaseColor} state={state} onClick={() => onNodeClick(l.lessonId)} lessons={lessons} />
            </div>
          </div>
        );
      })}

      <TreeSectionRule />
      <div tabIndex={-1} aria-label={`Infinite Reps — ${courseComplete ? 'unlocked' : 'locked'}`}>
        <InfiniteRepsNode unlocked={courseComplete} />
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   LEGEND — no box; a border-top divider (applied by its parent) matches
   the right column's width, not the full page (correction 1).
══════════════════════════════════════════ */
const LEGEND_ITEMS = [
  { state: 'lit', label: 'Lit — complete' },
  { state: 'current', label: 'Current — in progress' },
  { state: 'available', label: 'Available — up next' },
  { state: 'sealed', label: 'Sealed — locked' },
];

const LegendGlyph = ({ state }) => {
  const s = {
    lit: { background: TT.orange, border: TT.orange, borderStyle: 'solid' },
    current: { background: TT.bgSurface, border: TT.orange, borderStyle: 'solid', boxShadow: `0 0 0 2px ${TT.orangeGlow}` },
    available: { background: TT.bg, border: `${TT.orange}80`, borderStyle: 'solid' },
    sealed: { background: TT.bg, border: TT.border, borderStyle: 'dashed' },
  }[state];
  return (
    <div style={{
      width: 14, height: 14, borderRadius: '50%',
      border: `1.5px ${s.borderStyle} ${s.border}`, background: s.background, boxShadow: s.boxShadow,
    }} />
  );
};

// No fixed/narrow width on the trailing text — "Last: 106 days ago" needs
// to comfortably coexist with "Last: 1 year ago" etc. without truncating
// (v8 correction 2 — relocated here from the manifest panel's old pills
// row, which was removed as duplication, correction 1).
// v10: below isWide the 4 items become a 2x2 grid instead of a wrapping
// row (which pushed the page wider than the viewport), the trailing stats
// line drops to its own full-width row with marginLeft:0 (was 'auto') and
// flexShrink:1 so it can wrap instead of forcing overflow, and "unlimited
// reps" — marketing copy in a stats line — is dropped since it's what
// pushed the row past the viewport edge in the first place.
const MapLegend = ({ skill, lessonsTotal, getLastPracticed, isWide }) => (
  <div style={{ display: 'flex', flexDirection: isWide ? 'row' : 'column', flexWrap: 'wrap', alignItems: isWide ? 'center' : 'stretch', gap: isWide ? 20 : 12 }}>
    <div style={{
      display: isWide ? 'flex' : 'grid',
      gridTemplateColumns: isWide ? undefined : '1fr 1fr',
      flexWrap: 'wrap', gap: isWide ? 16 : 10,
    }}>
      {LEGEND_ITEMS.map(({ state, label }) => (
        <div key={state} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <LegendGlyph state={state} />
          <span style={{ fontSize: 11, color: TT.textMuted }}>{label}</span>
        </div>
      ))}
    </div>
    <div style={{
      marginLeft: isWide ? 'auto' : 0, flexShrink: 1,
      fontSize: 11, color: TT.textDim, fontFamily: TT.fontMono,
    }}>
      {skill.phases.length} phases · {lessonsTotal} modules{isWide ? ' · unlimited reps' : ''} · Last: {getLastPracticed() || 'never'}
    </div>
    {/* Dev-only, VITE_UNLOCK_ALL=true only — a persistent, impossible-to-
        miss reminder that node sealing is off, so this never gets read as
        real learner progress. */}
    {UNLOCK_ALL_DEV && (
      <div style={{
        width: '100%', fontSize: 10.5, color: TT.textDim, fontFamily: TT.fontMono,
        opacity: 0.55, letterSpacing: '0.02em',
      }}>
        unlocked (dev)
      </div>
    )}
  </div>
);

/* ══════════════════════════════════════════
   MAIN
══════════════════════════════════════════ */
const PromptLanding = () => {
  usePageTitle('AI Prompt Engineering | Castle Atlas');
  // Unconditionally dark terminal surface (backgroundColor: TT.bg below,
  // same token PromptSandbox uses) regardless of the global theme toggle —
  // so the floating rail should read dark while this page is mounted,
  // independent of the user's own light/dark setting.
  useForceSurface('dark');
  const navigate = useNavigate();
  const { completedExercises, loading: progressLoading } = useProgress();
  const { streak } = useStreak();
  const { skills, loading: skillsLoading } = useSkills();
  // Single, stable calls — lessons/techniques/mastery are passed down as
  // props from here rather than each node/panel calling its own hook
  // independently (prompt 5 audit finding: 13 lesson nodes + the manifest
  // panel + the map canvas were each firing their own useLessons() fetch,
  // and most of those independent effects were losing the race against
  // their own cleanup before ever calling setLessons — see item 1 in the
  // prompt 5 report for the full diagnosis. One call here, passed down,
  // both fixes the display bug and removes 15+ redundant network calls).
  const { lessons, loading: lessonsLoading, error: lessonsError } = useLessons();
  const { techniques } = useTechniques();
  const { mastery } = useTechniqueMastery();

  const skillRow = skills?.find(s => s.id === 'ai-prompting');
  const skill = {
    id: 'ai-prompting',
    name: skillRow?.name || 'AI Prompt Engineering',
    description: skillRow?.description
      || 'Master the one skill that multiplies every other tool you use — scored in real time by AI.',
    phases: PHASES,
  };

  const [radarOpen, setRadarOpen] = useState(false);
  const isWide = useMediaQuery(WIDE_QUERY);
  const isManifestSideBySide = useMediaQuery(MANIFEST_SIDE_BY_SIDE_QUERY);
  // Declared here (with the other hooks, before the loading early-return
  // below) rather than next to handleToggleRadar where it's used — hooks
  // must run unconditionally on every render, and this file already learned
  // that lesson once (see PromptSandbox's git history: "correct hook order,
  // all hooks before early returns").
  const radarRef = useRef(null);
  // Prompt 4 Part B4: due-review count + queue, visible on the map. Same
  // hooks-before-early-return rule as above.
  const dueReviews = useDueReviews();
  const [reviewsPanelOpen, setReviewsPanelOpen] = useState(false);

  if (progressLoading || skillsLoading || lessonsLoading) return <SkeletonLoader />;

  // Prompt 6, item 1: a failed lessons fetch used to be invisible — the
  // page rendered on with an empty lessons array, and every fallback
  // string (raw lesson id as the number, "Lesson N" as the name, a
  // hardcoded rep count) looked exactly like real content. Show an
  // explicit error instead of the map, rather than silently rendering
  // placeholders a learner could mistake for the truth.
  if (lessonsError) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: TT.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ maxWidth: 420, textAlign: 'center', fontFamily: TT.fontMono }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#ef4444', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Couldn't load this skill
          </div>
          <p style={{ fontSize: 13, color: TT.textMuted, lineHeight: 1.6, marginBottom: 16 }}>
            {lessonsError.unreachable
              ? "Can't reach the server — it's either down or not running locally. Start it and reload."
              : "The server returned an error loading lesson data. Reloading usually fixes it — if it keeps happening, that's worth reporting."}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px', background: 'transparent', color: TT.orange,
              border: `1px solid ${TT.orange}`, borderRadius: TT.radius.sm,
              fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: TT.fontMono,
            }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }

  /* ── Derived state ── */
  const getLessonDone = id => completedExercises.filter(e => e.lesson_id === id).length;

  const skillLessonIds = new Set(skill.phases.flatMap(p => p.lessons));
  const skillExercises = completedExercises.filter(e => skillLessonIds.has(e.lesson_id));
  const hasStarted = skillExercises.length > 0;

  const getResumeTarget = () => {
    for (const phase of skill.phases) {
      for (const lid of phase.lessons) {
        const d = getLessonDone(lid);
        if (d < repCountFor(lessons, lid)) return { lessonId: lid, repNum: d + 1, phaseNum: phase.num };
      }
    }
    return null;
  };
  const resumeTarget = getResumeTarget();

  const getLastPracticed = () => {
    if (!skillExercises.length) return null;
    const last = skillExercises.reduce((l, ex) =>
      new Date(ex.completed_at) > new Date(l.completed_at) ? ex : l
    );
    const diff = Math.floor((new Date() - new Date(last.completed_at)) / (1000 * 60 * 60 * 24));
    return diff === 0 ? 'today' : diff === 1 ? 'yesterday' : `${diff} days ago`;
  };

  const radarData = buildPhaseRadarData(techniques, mastery);

  const lessonsTotal = FLATTENED_LESSONS.length; // 13
  const lessonsComplete = FLATTENED_LESSONS.filter(l => getLessonDone(l.lessonId) >= repCountFor(lessons, l.lessonId)).length;
  const lessonsPct = lessonsTotal > 0 ? Math.round((lessonsComplete / lessonsTotal) * 100) : 0;
  // Infinite Reps is the only other thing this variable unlocks (see its
  // two usages below) — folding the dev flag in here keeps both node
  // types honoring the same override without a second prop to thread.
  const courseComplete = lessonsComplete === lessonsTotal || UNLOCK_ALL_DEV;

  const currentOrdinal = resumeTarget
    ? FLATTENED_LESSONS.findIndex(l => l.lessonId === resumeTarget.lessonId)
    : -1;

  const getNodeState = (lessonId) => {
    const done = getLessonDone(lessonId);
    if (done >= repCountFor(lessons, lessonId)) return 'lit';
    // Dev-only unlock: real completion (above) still renders 'lit', and
    // the actual resume point still renders 'current' — the only change
    // is that nothing past it ever comes back 'sealed'. Never touches
    // getLessonDone/repCountFor, so scoring and progress are unaffected.
    if (UNLOCK_ALL_DEV) return (resumeTarget && lessonId === resumeTarget.lessonId) ? 'current' : 'available';
    if (!resumeTarget) return 'lit';
    if (lessonId === resumeTarget.lessonId) return 'current';
    const ordinal = FLATTENED_LESSONS.findIndex(l => l.lessonId === lessonId);
    if (ordinal === currentOrdinal + 1) return 'available';
    // Unsealed: every node past the resume point is 'available' rather
    // than 'sealed'. This is the one line that ever produced 'sealed' —
    // sealing styling/state handling (style maps, Lock icon, dashed
    // borders, legend entry) is untouched and still fully wired, so
    // reverting just this line switches sealing back on.
    return 'available';
  };

  const handleNodeClick = (lessonId) => {
    const done = getLessonDone(lessonId);
    const position = Math.min(done + 1, repCountFor(lessons, lessonId));
    navigate(`/skills/ai-prompting/lessons/${lessonId}/reps/${position}`);
  };

  const handleResume = () => {
    if (resumeTarget) {
      navigate(`/skills/ai-prompting/lessons/${resumeTarget.lessonId}/reps/${resumeTarget.repNum}`);
    } else {
      navigate('/skills/ai-prompting/lessons/1/reps/1');
    }
  };

  // v11: opening the radar auto-scrolls it into view (radarRef declared up
  // with the other hooks above). Moving ManifestRadar to mount right after
  // SkillBelt when stacked (see MANIFEST_SIDE_BY_SIDE_QUERY above) fixes
  // the "chart renders off-screen above the button" bug, but at narrow
  // widths the belt's own content (ring+button row, then the stats grid)
  // still puts real vertical distance between the button and the chart
  // below it — measured on a 390x844 viewport, the chart landed entirely
  // below the fold with zero scrolling. The quality floor calls for the
  // chart to be visible without the user scrolling; the only way to
  // actually deliver that given the belt's real height is to have the
  // toggle itself bring it into view.
  const handleToggleRadar = () => {
    setRadarOpen((o) => {
      const next = !o;
      if (next) {
        const reduceMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        requestAnimationFrame(() => {
          radarRef.current?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
        });
      }
      return next;
    });
  };

  return (
    // v5 correction 2: minHeight + background applied directly to this
    // component's own root, not relying on the external .page-container
    // class (which only provides max-width/margin/padding, no min-height) —
    // that's what left a solid band showing through below short content.
    // v7 correction 1: this root is now a flex column so the body row
    // below can stretch with flex:1 and actually reach the true page
    // bottom, instead of just sizing to its own content height. v7
    // correction 2: padding is a full explicit override (not just
    // paddingBottom) so the external .page-container class's 40px top
    // padding — the actual cause of the breadcrumb sitting too far down —
    // no longer applies.
    <div style={{
      minHeight: '100vh',
      backgroundColor: TT.bg,
      backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)',
      backgroundSize: '22px 22px',
    }}>
    <div className="page-container rail-clearance" style={{
      maxWidth: '100%', minHeight: '100vh', padding: '0 24px 80px',
      display: 'flex', flexDirection: 'column',
      fontFamily: TT.fontSans,
      boxSizing: 'border-box',
    }}>
      <style>{`
        @keyframes termIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes pulse-dot{0%,100%{box-shadow:0 0 0 0 rgba(34,197,94,0.4)}50%{box-shadow:0 0 0 4px rgba(34,197,94,0)}}
        /* flex:1 so this wrapper actually grows to fill the page root's
           min-height:100vh (v7 correction 1) instead of just sizing to
           its own content height. */
        .t1{animation:termIn 0.35s ease-out both; display:flex; flex-direction:column; flex:1;}

        /* No outer box (correction 1, v5) — manifest panel is a plain
           column with a single divider, not a bordered rect. flex:1 +
           min-height:0 (v7) so this row actually stretches down to the
           true page bottom rather than stopping at its content height —
           align-items:stretch then makes both columns match that height. */
        .pl-body { display: flex; align-items: stretch; flex: 1; min-height: 0; }
        .pl-manifest { width: 380px; flex-shrink: 0; border-right: 1px solid ${TT.border}; display: flex; flex-direction: column; }
        /* v11: 879px, the exact complement of MANIFEST_SIDE_BY_SIDE_QUERY's
           min-width:880px — see that constant's comment for why the two
           queries must never overlap or gap at the boundary pixel. */
        @media (max-width: 879px) {
          .pl-body { flex-direction: column; }
          .pl-manifest { width: 100%; border-right: none; border-bottom: 1px solid ${TT.border}; }
        }

        /* v10: breadcrumb costs nothing at >=768px (a hamburger + back
           gesture already cover navigation below that), so it only shows
           there. Hiding it (not just visually, removed from flex flow)
           leaves the streak badge as the row's only flex item, which
           "space-between" naturally left-aligns — no extra rule needed to
           move it to the edge. */
        @media (max-width: 767px) {
          .pl-breadcrumb { display: none; }
        }

        /* v10: keyboard focus ring for the vertical tree's node wrapper —
           LessonNode itself has no tabIndex/onKeyDown (same as in the
           existing zigzag map), so the tree's positioning wrapper owns
           focusability instead of editing LessonNode. */
        .tree-node-focus { border-radius: ${TT.radius.md}px; }
        .tree-node-focus:focus { outline: none; }
        .tree-node-focus:focus-visible { outline: 2px solid ${TT.orange}; outline-offset: 2px; }

        @media (prefers-reduced-motion: reduce) {
          .t1, .t1 * { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; }
        }
      `}</style>

      <div className="t1">
        {/* Top strip — no box, just a divider below (correction 1, v5).
            Vertical padding trimmed (v7 correction 2) so the breadcrumb
            sits close to the real top edge. v10: XP badge removed entirely
            (see WIDE_QUERY-adjacent notes above) — it advertised a feature
            that doesn't exist on the page whose whole job is getting
            someone to start lesson 1; re-add a real one when there's a
            real XP field. */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
          flexWrap: 'wrap', padding: '10px 22px', borderBottom: `1px solid ${TT.border}`,
        }}>
          <div className="pl-breadcrumb">
            <Breadcrumb navigate={navigate} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ReviewsBadge
              count={dueReviews.count} reviews={dueReviews.reviews} loading={dueReviews.loading}
              open={reviewsPanelOpen} onToggle={() => setReviewsPanelOpen(o => !o)} navigate={navigate}
            />
            <StreakBadge streak={streak} />
          </div>
        </div>

        {/* Body: manifest panel (left, full height) | right column (belt → map → legend) */}
        <div className="pl-body">
          <div className="pl-manifest">
            <ManifestPanel
              skill={skill} resumeTarget={resumeTarget}
              lessonsPct={lessonsPct} lessonsComplete={lessonsComplete} lessonsTotal={lessonsTotal}
              onResume={handleResume} lessons={lessons}
            />
            {/* v11: only mounts here when the manifest column is side-by-side
                with the right column. Stacked (below 880px), it mounts in the
                right column instead, right after SkillBelt — see there.
                Wrapped so the same radarRef works at either mount point
                (only one is ever rendered at a time) — the wrapper carries
                the flex:1 that ManifestRadar's own root also declares, so
                nesting it doesn't change the effective box model. */}
            {isManifestSideBySide && (
              <div ref={radarRef} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <ManifestRadar open={radarOpen} hasStarted={hasStarted} radarData={radarData} />
              </div>
            )}
          </div>

          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '20px 22px' }}>
              <SkillBelt pct={lessonsPct} radarData={radarData} radarOpen={radarOpen} onToggleRadar={handleToggleRadar} isWide={isWide} />
            </div>

            {/* v11: stacked-column mount point — fixes the v10-flagged bug
                where tapping Radar below 880px rendered the chart in the
                manifest column, which sits ABOVE the belt once stacked, so
                the chart opened off-screen above the button that opened it. */}
            {!isManifestSideBySide && (
              <div ref={radarRef} style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <ManifestRadar open={radarOpen} hasStarted={hasStarted} radarData={radarData} />
              </div>
            )}

            {/* flex:1 so this section (not the header) absorbs the leftover
                vertical space, and the inner wrapper centers NodeMapCanvas
                within it (v8 correction 3) — the "Learning Path" header
                itself stays pinned at the top. v10: below isWide the map
                swaps for the vertical tree, which is natural block flow
                (no fixed aspect-ratio box to center), so it skips the
                flex:1/justifyContent:center centering wrapper entirely. */}
            <div style={{ padding: '20px 22px', ...(isWide ? { flex: 1, minHeight: 0 } : {}), display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <Map size={13} color={TT.orange} />
                <span style={{ fontSize: 10, fontWeight: 800, color: TT.textDim, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  Learning Path
                </span>
              </div>
              {isWide ? (
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <NodeMapCanvas getLessonDone={getLessonDone} getNodeState={getNodeState} onNodeClick={handleNodeClick} courseComplete={courseComplete} lessons={lessons} />
                </div>
              ) : (
                <VerticalTree getLessonDone={getLessonDone} getNodeState={getNodeState} onNodeClick={handleNodeClick} courseComplete={courseComplete} lessons={lessons} />
              )}
            </div>

            {/* margin-top: auto pins this to the true bottom of the now
                full-height column (v7 correction 1a) — any leftover space
                becomes a gap above it instead of dead space below it. */}
            <div style={{ padding: '16px 22px', borderTop: `1px solid ${TT.border}`, marginTop: 'auto' }}>
              <MapLegend skill={skill} lessonsTotal={lessonsTotal} getLastPracticed={getLastPracticed} isWide={isWide} />
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default PromptLanding;
